import Avatar, {
  AvatarFallback,
  createAvatarFallback,
} from "@/lib/components/ui/avatar/avatar";
import {
  ColorField,
  ColorFieldPreview,
  ColorFieldSwatches,
} from "@/lib/components/ui/color-field";
import { normalizeHex } from "@/lib/components/ui/color-field/color-field.utils";
import { pickReadableForegroundOn } from "@/lib/theme/brand-contrast";
import { cn } from "@/lib/utils";
import { useUploadRestrictions } from "@/modules/uploads";
import { formatFileSize } from "@soulbyte/core";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { TenantLogo } from "./tenant-logo";

/**
 * LogoImage shows a company logo: a picked file before it is saved, the saved
 * logo, or the company's initials when there is none.
 */
export function LogoImage({
  url,
  name,
  className,
}: {
  url: string | undefined;
  name: string | undefined;
  className: string;
}) {
  // a picked file is a local blob, the saved logo needs the tenant fetch
  if (url?.startsWith("blob:")) {
    return (
      <img
        src={url}
        alt=""
        className={cn("shrink-0 rounded-md object-contain", className)}
      />
    );
  }
  return url ? (
    <TenantLogo
      alt=""
      logoUrl={url}
      className={cn("shrink-0 rounded-md object-contain", className)}
    />
  ) : (
    <Avatar className={cn("shrink-0 rounded-md", className)}>
      <AvatarFallback>{createAvatarFallback(name)}</AvatarFallback>
    </Avatar>
  );
}

/**
 * BrandingPreview is a bare sketch of the app: the module bar on the left in
 * the sidebar colour, the open conversation and your own message in the
 * brand colour, everything else as grey placeholders.
 */
export function BrandingPreview({
  sidebar,
  brand,
  logo,
}: {
  sidebar: string;
  brand: string;
  logo: ReactNode;
}) {
  const onSidebar = pickReadableForegroundOn(sidebar);
  const onBrand = pickReadableForegroundOn(brand);

  return (
    <div
      aria-hidden
      className="border-border bg-background flex aspect-[16/7] w-full overflow-hidden rounded-xl border"
    >
      <div
        className="flex w-12 shrink-0 flex-col items-center gap-3 py-3"
        style={{ backgroundColor: sidebar }}
      >
        <div className="size-7">{logo}</div>
        {[0, 1, 2].map((index) => (
          <span
            key={index}
            className="size-5 rounded-md opacity-30"
            style={{ backgroundColor: onSidebar }}
          />
        ))}
      </div>
      <div className="border-border flex w-1/4 shrink-0 flex-col gap-3 border-r p-3">
        <span
          className="flex h-6 items-center rounded-md px-2"
          style={{ backgroundColor: brand }}
        >
          <span
            className="h-1.5 w-3/5 rounded-full opacity-70"
            style={{ backgroundColor: onBrand }}
          />
        </span>
        <span className="bg-foreground/10 mx-2 h-1.5 w-1/2 rounded-full" />
        <span className="bg-foreground/10 mx-2 h-1.5 w-2/3 rounded-full" />
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <span
          className="ml-auto flex h-8 w-2/5 items-center rounded-2xl px-3"
          style={{ backgroundColor: brand }}
        >
          <span
            className="h-1.5 w-3/4 rounded-full opacity-70"
            style={{ backgroundColor: onBrand }}
          />
        </span>
        <span className="bg-foreground/10 h-1.5 w-3/5 rounded-full" />
        <span className="bg-foreground/10 h-1.5 w-2/5 rounded-full" />
        <span className="border-border mt-auto h-8 rounded-full border" />
      </div>
    </div>
  );
}

/** ColorPicker offers the colour choices as swatches, your own colour last. */
export function ColorPicker({
  label,
  choices,
  value,
  disabled,
  onChange,
}: {
  label: string;
  choices: readonly string[];
  value: string;
  disabled?: boolean;
  onChange: (hex: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <ColorField
      colors={[...choices]}
      value={value}
      disabled={disabled}
      onValueChange={onChange}
    >
      <div className="flex flex-wrap items-center gap-3">
        <ColorFieldSwatches
          groupAriaLabel={label}
          getSwatchAriaLabel={(hex) =>
            t("company_setup_color_swatch_label", { color: hex })
          }
        />
        {/* the last swatch is your own colour: always a rainbow, so it keeps
            saying "any colour"; a ring marks it picked, like the others */}
        <ColorFieldPreview
          pickerAriaLabel={t("settings_branding_custom_color")}
          className={cn(
            "size-6 rounded-full border-2 bg-[conic-gradient(red,yellow,lime,aqua,blue,magenta,red)] shadow-none",
            !choices.includes(value) &&
              "ring-foreground ring-offset-background ring-2 ring-offset-2",
          )}
        />
      </div>
    </ColorField>
  );
}

/** toHex returns the colour as a normalized hex value, or the fallback. */
export function toHex(raw: string | undefined, fallback: string) {
  return normalizeHex(raw ?? "") ?? fallback;
}

/**
 * useLogoFileRules reads the upload limits for a logo. checkLogoOrWarn toasts
 * why a picked file is refused and then returns false.
 */
export function useLogoFileRules() {
  const { t } = useTranslation();
  const restrictions = useUploadRestrictions().data?.file_types?.images;
  const maxBytes = restrictions?.max_bytes ?? 0;
  const accept = [
    ...(restrictions?.mime_types ?? []),
    ...(restrictions?.extensions ?? []),
  ].join(",");

  function checkLogoOrWarn(file: File) {
    if (!restrictions) {
      toast.error(t("upload_error_restrictions_loading"));
      return false;
    }
    if (!isAllowedImage(file, restrictions)) {
      toast.error(t("company_setup_logo_invalid_format"));
      return false;
    }
    if (file.size > maxBytes) {
      toast.error(
        t("company_setup_logo_file_too_large", {
          maxSize: formatFileSize(maxBytes),
        }),
      );
      return false;
    }
    return true;
  }

  return { accept, maxBytes, checkLogoOrWarn };
}

/**
 * isAllowedImage says whether a file may be uploaded as a logo, by its type or,
 * when the browser reports none, by its extension.
 */
export function isAllowedImage(
  file: File,
  restrictions: { mime_types?: string[]; extensions?: string[] },
) {
  if ((restrictions.mime_types ?? []).includes(file.type)) return true;
  const dot = file.name.lastIndexOf(".");
  const extension = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
  if (!(restrictions.extensions ?? []).includes(extension)) return false;
  return file.type === "" || file.type === "application/octet-stream";
}
